import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/Table';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { formatCurrency, cn } from '../lib/utils';
import { Edit2, Save, X, Users, Plus, Layers } from 'lucide-react';
import { MOCK_USERS, INITIAL_CDS_TIERS } from '../data/mockData';
interface CDSTiersProps {
  focusUserId?: string | null;
  onConsumeFocus?: () => void;
}
export function CDSTiers({
  focusUserId,
  onConsumeFocus
}: CDSTiersProps) {
  const [tiers, setTiers] = useState(INITIAL_CDS_TIERS);
  const [highlightedUserId, setHighlightedUserId] = useState<string | null>(null);
  const focusedRowRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    if (focusUserId) {
      setHighlightedUserId(focusUserId);
      onConsumeFocus?.();
      const timer = setTimeout(() => focusedRowRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      }), 100);
      const clear = setTimeout(() => setHighlightedUserId(null), 2500);
      return () => {
        clearTimeout(timer);
        clearTimeout(clear);
      };
    }
  }, [focusUserId, onConsumeFocus]);
  const [editingTierId, setEditingTierId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    min: number;
    max: number | null;
    amount: number;
  } | null>(null);
  const cdsUsers = MOCK_USERS.filter(u => u.model === 'CDS');
  const handleEdit = (tier: (typeof INITIAL_CDS_TIERS)[0]) => {
    setEditingTierId(tier.id);
    setEditForm({
      min: tier.min,
      max: tier.max,
      amount: tier.amount
    });
  };
  const handleCancel = () => {
    setEditingTierId(null);
    setEditForm(null);
  };
  const handleSave = (id: string) => {
    if (!editForm) return;
    setTiers(tiers.map(t => t.id === id ? {
      ...t,
      ...editForm
    } : t));
    setEditingTierId(null);
    setEditForm(null);
  };
  return <div className="space-y-6" data-id="element-46">
      <div className="flex items-center justify-between pb-2" data-id="element-47">
        <div data-id="element-48">
          <h1 className="text-2xl font-semibold text-slate-900" data-id="element-49">
            CDS Commission Tiers
          </h1>
          <p className="text-sm text-slate-500" data-id="element-50">
            Manage flat-fee tiers based on funded loan volume for CDS users.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-51">
        {/* Left: Tier Structure (Takes up 5 columns on large screens) */}
        <Card className="lg:col-span-5 shadow-sm border-slate-200/60 h-fit rounded-xl" data-id="element-52">
          <CardHeader className="flex flex-row items-center justify-between bg-white rounded-t-xl border-b border-slate-100 pb-4" data-id="element-53">
            <div data-id="element-54">
              <CardTitle className="flex items-center" data-id="element-55">
                <Layers className="mr-2 h-5 w-5 text-blue-500" data-id="element-56" />
                Tier Structure
              </CardTitle>
              <CardDescription data-id="element-57">
                Global tier settings for all CDS users
              </CardDescription>
            </div>
            <Button size="sm" variant="outline" className="h-8 shadow-sm" data-id="element-58">
              <Plus className="mr-2 h-4 w-4" data-id="element-59" /> Add Tier
            </Button>
          </CardHeader>
          <CardContent className="p-0" data-id="element-60">
            <Table data-id="element-61">
              <TableHeader className="bg-slate-50/50" data-id="element-62">
                <TableRow className="hover:bg-transparent" data-id="element-63">
                  <TableHead className="pl-6 font-semibold text-slate-600" data-id="element-64">
                    Volume Range (Files)
                  </TableHead>
                  <TableHead className="text-right font-semibold text-slate-600" data-id="element-65">
                    Per Loan Amount
                  </TableHead>
                  <TableHead className="w-[80px] pr-6" data-id="element-66"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-67">
                {tiers.map(tier => {
                const isEditing = editingTierId === tier.id;
                return <TableRow key={tier.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-68">
                      <TableCell className="font-medium pl-6 py-4" data-id="element-69">
                        {isEditing && editForm ? <div className="flex items-center space-x-2" data-id="element-70">
                            <Input type="number" className="w-16 h-8 text-center font-semibold" value={editForm.min} onChange={e => setEditForm({
                        ...editForm,
                        min: parseInt(e.target.value) || 0
                      })} data-id="element-71" />
                            <span className="text-slate-400 font-medium" data-id="element-72">
                              to
                            </span>
                            <Input type="number" className="w-16 h-8 text-center font-semibold" value={editForm.max || ''} placeholder="+" onChange={e => setEditForm({
                        ...editForm,
                        max: e.target.value ? parseInt(e.target.value) : null
                      })} data-id="element-73" />
                          </div> : <span className="text-slate-700 font-semibold" data-id="element-74">
                            {tier.min} {tier.max ? `- ${tier.max}` : '+'}{' '}
                            <span className="text-slate-400 font-normal ml-1" data-id="element-75">
                              Loans
                            </span>
                          </span>}
                      </TableCell>
                      <TableCell className="text-right" data-id="element-76">
                        {isEditing && editForm ? <div className="flex items-center justify-end" data-id="element-77">
                            <span className="mr-2 text-slate-400 font-medium" data-id="element-78">
                              $
                            </span>
                            <Input type="number" className="w-24 h-8 text-right font-semibold" value={editForm.amount} onChange={e => setEditForm({
                        ...editForm,
                        amount: parseInt(e.target.value) || 0
                      })} data-id="element-79" />
                          </div> : <span className="font-bold text-slate-900 text-base" data-id="element-80">
                            {formatCurrency(tier.amount)}
                          </span>}
                      </TableCell>
                      <TableCell className="text-right pr-6" data-id="element-81">
                        {isEditing ? <div className="flex items-center justify-end space-x-1" data-id="element-82">
                            <Button size="icon" variant="ghost" onClick={() => handleSave(tier.id)} className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-id="element-83">
                              <Save className="h-4 w-4" data-id="element-84" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={handleCancel} className="h-8 w-8 text-slate-400 hover:text-slate-600" data-id="element-85">
                              <X className="h-4 w-4" data-id="element-86" />
                            </Button>
                          </div> : <Button size="icon" variant="ghost" onClick={() => handleEdit(tier)} className="h-8 w-8 text-slate-400 hover:text-slate-600" data-id="element-87">
                            <Edit2 className="h-4 w-4" data-id="element-88" />
                          </Button>}
                      </TableCell>
                    </TableRow>;
              })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Right: Active Users (Takes up 7 columns on large screens) */}
        <Card className="lg:col-span-7 flex flex-col h-[600px] shadow-sm border-slate-200/60 rounded-xl" data-id="element-89">
          <CardHeader className="shrink-0 bg-white rounded-t-xl border-b border-slate-100 pb-4" data-id="element-90">
            <CardTitle className="flex items-center" data-id="element-91">
              <Users className="mr-2 h-5 w-5 text-blue-500" data-id="element-92" />
              Active CDS Users ({cdsUsers.length})
            </CardTitle>
            <CardDescription data-id="element-93">
              Current MTD performance for CDS designated users
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0" data-id="element-94">
            <Table data-id="element-95">
              <TableHeader className="sticky top-0 bg-slate-50/90 backdrop-blur-sm shadow-sm z-10 border-b border-slate-200" data-id="element-96">
                <TableRow className="hover:bg-transparent" data-id="element-97">
                  <TableHead className="pl-6 font-semibold text-slate-600" data-id="element-98">
                    User
                  </TableHead>
                  <TableHead className="text-right font-semibold text-slate-600" data-id="element-99">
                    Funded (MTD)
                  </TableHead>
                  <TableHead className="text-right pr-6 font-semibold text-slate-600" data-id="element-100">
                    Est. Commission
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-101">
                {cdsUsers.map(user => {
                const currentTier = tiers.find(t => user.fundedMTD >= t.min && (t.max === null || user.fundedMTD <= t.max));
                const estimatedCommission = currentTier ? user.fundedMTD * currentTier.amount : 0;
                const isHighlighted = highlightedUserId === user.id;
                return <TableRow key={user.id} ref={isHighlighted ? focusedRowRef : undefined} className={cn('transition-colors', isHighlighted ? 'bg-blue-50 ring-1 ring-inset ring-blue-200' : 'hover:bg-slate-50/50')} data-id="element-102">
                      <TableCell className="font-medium pl-6 py-3" data-id="element-103">
                        <div className="flex items-center gap-4" data-id="element-104">
                          <img src={user.avatar} alt={user.name} className="h-10 w-10 rounded-full bg-slate-200 shadow-sm border border-slate-100" data-id="element-105" />
                          <div data-id="element-106">
                            <div className="text-sm font-semibold text-slate-900" data-id="element-107">
                              {user.name}
                            </div>
                            <div className="text-xs text-slate-500 font-medium" data-id="element-108">
                              {user.title}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right" data-id="element-109">
                        <div className="flex flex-col items-end" data-id="element-110">
                          <span className="font-bold text-slate-700 text-base" data-id="element-111">
                            {user.fundedMTD}
                          </span>
                          {currentTier && <span className="text-xs font-medium text-slate-400 mt-0.5" data-id="element-112">
                              Tier:{' '}
                              <span className="text-slate-500" data-id="element-113">
                                {formatCurrency(currentTier.amount)}/ea
                              </span>
                            </span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold pr-6 text-slate-900 text-base" data-id="element-114">
                        {formatCurrency(estimatedCommission)}
                      </TableCell>
                    </TableRow>;
              })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>;
}